import React, { useState, useEffect, useContext } from 'react';
import { Table, Button, Drawer, Form, Input, Select, message } from 'antd';
import axios from 'axios';
import { LoginContext } from '../../context/loginContext';

const { Option } = Select;

const CUsuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form] = Form.useForm();
  const { user } = useContext(LoginContext);

  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');

  useEffect(() => {
    fetchUsuarios();
  }, []);

  const fetchUsuarios = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/usuarios`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      setUsuarios(response.data);
    } catch (error) {
      console.error('Error al obtener usuarios:', error);
    }
  };

  const openDrawer = (user = null) => {
    setEditUser(user);
    setDrawerVisible(true);
    if (user) {
      const userDataWithoutPassword = { ...user };
      delete userDataWithoutPassword.contrasenia;
      form.setFieldsValue(userDataWithoutPassword);
    } else {
      form.resetFields();
    }
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    form.resetFields();
  };

  const handleSaveUser = async (values) => {
    try {
      const headers = {
        Authorization: `Bearer ${user.token}`,
      };

      if (editUser) {
        const updateData = { ...values };
        if (!updateData.contrasenia) {
          delete updateData.contrasenia;
        }
        await axios.put(
          `${process.env.REACT_APP_API_URL}/usuarios/${editUser._id}`,
          updateData,
          { headers }
        );
        message.success('Usuario actualizado correctamente');
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/usuarios`, values, { headers });
        message.success('Usuario creado correctamente');
      }

      fetchUsuarios();
      closeDrawer();
    } catch (error) {
      message.error('Error al guardar usuario');
      console.error('Error al guardar usuario:', error);
    }
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Buscar ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)} size="small">
            Buscar
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small">
            Resetear
          </Button>
        </div>
      </div>
    ),
    filterIcon: (filtered) => <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>,
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    render: (text) =>
      searchedColumn === dataIndex ? (
        <span style={{ backgroundColor: '#ffc069', padding: '0 5px' }}>{text}</span>
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: 'Usuario',
      dataIndex: 'usuario',
      key: 'usuario',
      ...getColumnSearchProps('usuario'),
    },
    {
      title: 'Razón Social',
      dataIndex: 'razonSocial',
      key: 'razonSocial',
      ...getColumnSearchProps('razonSocial'),
    },
    {
      title: 'Celular',
      dataIndex: 'celular',
      key: 'celular',
      ...getColumnSearchProps('celular'),
    },
    {
      title: 'CUIT',
      dataIndex: 'cuit',
      key: 'cuit',
      ...getColumnSearchProps('cuit'),
    },
    {
      title: 'Tipo de Usuario',
      dataIndex: 'tipoUsuario',
      key: 'tipoUsuario',
      ...getColumnSearchProps('tipoUsuario'),
      render: (tipo) => (tipo === 'administrador' ? 'Administrador' : 'Cliente'),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (text, record) => (
        <Button type="link" onClick={() => openDrawer(record)}>
          Editar
        </Button>
      ),
    },
  ];

  return (
    <>
      <Button type="primary" onClick={() => openDrawer()} style={{ marginBottom: 16 }}>
        Agregar Usuario
      </Button>

      <Table columns={columns} dataSource={usuarios} rowKey="_id" />

      <Drawer
        title={editUser ? 'Editar Usuario' : 'Agregar Usuario'}
        width={400}
        onClose={closeDrawer}
        visible={drawerVisible}
        bodyStyle={{ paddingBottom: 80 }}
      >
        <Form form={form} layout="vertical" onFinish={handleSaveUser}>
          <Form.Item
            name="usuario"
            label="Usuario"
            rules={[{ required: true, message: 'Por favor ingresa un nombre de usuario' }]}
          >
            <Input placeholder="Usuario" />
          </Form.Item>

          <Form.Item
            name="razonSocial"
            label="Razón Social"
            rules={[{ required: true, message: 'Por favor ingresa una razón social' }]}
          >
            <Input placeholder="Razón Social" />
          </Form.Item>

          <Form.Item
            name="contrasenia"
            label={editUser ? "Nueva Contraseña (opcional)" : "Contraseña"}
            rules={[{ required: !editUser, message: editUser ? '' : 'Por favor ingresa una contraseña' }]}
          >
            <Input.Password
              placeholder={editUser ? "Dejar en blanco para mantener la actual" : "Contraseña"}
            />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, message: 'Por favor ingresa un email' }]}
          >
            <Input placeholder="Email" />
          </Form.Item>

          <Form.Item
            name="celular"
            label="Celular"
            rules={[{ required: true, message: 'Por favor ingresa un número de celular' }]}
          >
            <Input placeholder="Celular" />
          </Form.Item>

          <Form.Item
            name="direccion"
            label="Dirección"
            rules={[{ required: true, message: 'Por favor ingresa una dirección' }]}
          >
            <Input placeholder="Dirección" />
          </Form.Item>

          <Form.Item
            name="cuit"
            label="CUIT"
            rules={[{ required: true, message: 'Por favor ingresa un CUIT válido' }]}
          >
            <Input placeholder="CUIT" />
          </Form.Item>

          <Form.Item
            name="tipoUsuario"
            label="Tipo de Usuario"
            rules={[{ required: true, message: 'Por favor selecciona un tipo de usuario' }]}
          >
            <Select placeholder="Selecciona un tipo">
              <Option value="administrador">Administrador</Option>
              <Option value="cliente">Cliente</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              {editUser ? 'Actualizar Usuario' : 'Crear Usuario'}
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
};

export default CUsuarios;